import Button from "@/utils/ui/Button/Button";
import { Film, Tag, Speech, Tags, Languages } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../store";
import MoreVert from "../../../assets/icons/svg/moreVert.svg?react";
import Modal from "@/components/Modal/Modal";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";
import AddNew from "@/components/MetaManagement/AddNew";
import { useClickOutside } from "../../../utils";
import {
  createCategory,
  updateCategory,
  getCategoryList,
  deleteCategory,
} from "@/store/media/media.action";
import PageHeader from "@/components/Layout/PageHeader";
import SidebarNavigation, {
  SidebarMenuItem,
} from "@/components/SidebarNavigation/SidebarNavigation";

const listMoreActions = ["edit", "delete"];
const subMetaList = ["delete"];

// Modal types enum
enum ModalType {
  NONE = "none",
  ADD_EDIT = "add_edit",
  CONFIRM = "confirm",
}

const MetaManagement = ({
  categories,
  createCategory,
  updateCategory,
  bunchLanguageList,
  categoryList,

  getCategoryList,
  bunchCategoryList,
  deleteCategory,
  tag,
  cast,
  genre,
}: Props) => {
  const languageRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const castRef = useRef<HTMLDivElement>(null);
  const genreRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const sidebarMenuItems: SidebarMenuItem[] = [
    {
      id: "language",
      label: "Language",
      icon: Languages,
      sectionRef: languageRef,
      url: "/meta-managements/language?settings",
    },
    {
      id: "category",
      label: "Category",
      icon: Tags,
      sectionRef: categoryRef,
      url: "/meta-managements/category?settings",
    },
    {
      id: "cast",
      label: "Cast",
      icon: Speech,
      sectionRef: castRef,
      url: "/meta-managements/cast?settings",
    },
    {
      id: "genre",
      label: "Genre",
      icon: Film,
      sectionRef: genreRef,
      url: "/meta-managements/genre?settings",
    },
    {
      id: "tags",
      label: "Tags",
      icon: Tag,
      sectionRef: tagsRef,
      url: "/meta-managements/tags?settings",
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const showBackArrow = new URLSearchParams(location.search).has("settings");
  const [modalType, setModalType] = useState<ModalType>(ModalType.NONE);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const moreOptionsRef = useRef<HTMLDivElement>(null);
  const subMetaMoreOptionsRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<any>(null);
  const [confirmModalItem, setConfirmModalItem] = useState<any>(null);
  const [itemDeleted, setItemDeleted] = useState<any>(null);
  const [isDelete, setIsDelete] = useState<boolean>(false);

  const [search, setSearch] = useState("");
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [openSubDropdown, setOpenSubDropdown] = useState<string | null>(null);
  const [hasName, setHasName] = useState<boolean>(false);
  const [metaType, setMetaType] = useState<string>("");
  const [activeSection, setActiveSection] = useState<string>("");

  const handleHasName = (e: any) => {
    setHasName(e);
  };

  const getCurrentMenuItem = () => {
    const currentPath = location.pathname + location.search;
    return sidebarMenuItems.find((item) => {
      if (!item.url) return false;
      const itemUrl = new URL(item.url, window.location.origin);
      const itemPath = itemUrl.pathname;
      const itemSearch = itemUrl.search;
      return currentPath === itemPath + itemSearch;
    });
  };

  const currentMenuItem = getCurrentMenuItem();

  useEffect(() => {
    if (currentMenuItem) {
      setMetaType(currentMenuItem.id);
      setActiveSection(currentMenuItem.id);
    }
  }, [currentMenuItem]);

  let filteredList: any[] = [];
  const currentItemId = currentMenuItem?.id;

  if (currentItemId === "language") {
    filteredList = Array.isArray(bunchLanguageList)
      ? bunchLanguageList.filter((item: any) =>
          (item.name || "").toLowerCase().includes(search.toLowerCase())
        )
      : [];
  } else if (currentItemId === "tags") {
    filteredList = Array.isArray(tag)
      ? tag.filter((item: any) =>
          (item.name || "").toLowerCase().includes(search.toLowerCase())
        )
      : [];
  } else if (currentItemId === "cast") {
    filteredList = Array.isArray(cast)
      ? cast.filter((item: any) =>
          (item.name || "").toLowerCase().includes(search.toLowerCase())
        )
      : [];
  } else if (currentItemId === "genre") {
    filteredList = Array.isArray(genre)
      ? genre.filter((item: any) =>
          (item.name || "").toLowerCase().includes(search.toLowerCase())
        )
      : [];
  } else {
    filteredList = Array.isArray(bunchCategoryList)
      ? bunchCategoryList.filter((item: any) => {
          const cat = (item?.name || "")
            .toLowerCase()
            .includes(search.toLowerCase());
          const sub = item?.sub_meta?.some((sub: any) =>
            (sub?.name || "").toLowerCase().includes(search.toLowerCase())
          );
          return cat || sub;
        })
      : [];
  }

  useClickOutside(moreOptionsRef, () => setOpenDropdown(null));
  useClickOutside(subMetaMoreOptionsRef, () => setOpenSubDropdown(null));

  const handleMoreOptionsToggle = (categoryKey: string) => {
    setOpenDropdown(openDropdown === categoryKey ? null : categoryKey);
  };

  const handleSubMetaMoreOptionsToggle = (subCatKey: string) => {
    setOpenSubDropdown(openSubDropdown === subCatKey ? null : subCatKey);
  };

  const handleMoreOptions = (option: string, item: any, sub?: any) => {
    if (option === "edit") {
      setModalType(ModalType.ADD_EDIT);
      setItems(item);
      setOpenDropdown(null);
      setOpenSubDropdown(null);
    } else if (option === "delete") {
      setModalType(ModalType.CONFIRM);
      if (sub) {
        // Handle sub-category deletion
        setConfirmModalItem({ item, sub });
        setIsDelete(false);
      } else {
        // Handle main category deletion
        setItemDeleted(item);
        setIsDelete(true);
      }
      setOpenDropdown(null);
      setOpenSubDropdown(null);
    }
  };

  const handleSubMetaMoreOptions = (
    e: any,
    option: string,
    item: any,
    sub: any
  ) => {
    e.stopPropagation();
    handleMoreOptions(option, item, sub);
  };

  const handleDeleteConfirm = async () => {
    await deleteCategory({ item: itemDeleted, type: metaType });
    setModalType(ModalType.NONE);
    setIsDelete(false);
  };

  const handleSave = (e: any) => {
    e.preventDefault();
    const event = new Event("saveCategory");
    window.dispatchEvent(event);
  };

  const handleDelete = async () => {
    const item = confirmModalItem.item;
    const sub = confirmModalItem.sub;
    const updatedItem = {
      ...item,
      sub_meta: item.sub_meta.filter((key: any) => key.name !== sub.name),
    };
    await updateCategory({ ...updatedItem, type: metaType });
    setModalType(ModalType.NONE);
  };

  const closeModal = () => {
    setModalType(ModalType.NONE);
    setItems(null);
    setConfirmModalItem(null);
    setItemDeleted(null);
    setIsDelete(false);
  };

  // Modal content based on type
  const getModalContent = () => {
    switch (modalType) {
      case ModalType.ADD_EDIT:
        return (
          <AddNew
            item={items}
            onClose={closeModal}
            onSave={createCategory}
            isCategory={currentItemId === "category"}
            handleHasName={handleHasName}
            isEdit={!!items}
            updateCategory={updateCategory}
            metaType={metaType}
            bunchCategoryList={bunchCategoryList}
            bunchLanguageList={bunchLanguageList}
          />
        );
      case ModalType.CONFIRM:
        return (
          <div className="d-flex flex-column justify-content-center align-items-center">
            <div className="my-3">
              <i className="bi bi-exclamation-triangle-fill fs-5 me-2 text-danger"></i>
              <span className="fs-5 text-danger">ALERT!</span>
            </div>
            <p>Are you sure you want to delete this content?</p>
          </div>
        );
      default:
        return null;
    }
  };

  // Modal footer based on type
  const getModalFooter = () => {
    switch (modalType) {
      case ModalType.ADD_EDIT:
        return (
          <Button
            type="outline-primary"
            onClick={handleSave}
            isDisabled={!hasName}
          >
            Save
          </Button>
        );
      case ModalType.CONFIRM:
        return (
          <div className="d-flex justify-content-center gap-2 w-100 align-items-center">
            <Button type="outline-primary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="primary"
              style={{ border: "none" }}
              onClick={isDelete ? handleDeleteConfirm : handleDelete}
            >
              Confirm
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  // Modal title based on type
  const getModalTitle = () => {
    switch (modalType) {
      case ModalType.ADD_EDIT:
        return `${items ? "Edit" : "Add"} ${metaType}`;
      case ModalType.CONFIRM:
        return "";
      default:
        return "";
    }
  };

  return (
    <div className="container">
      {/* <div className="subHeader pageHeader">
        <div className="d-flex align-items-center">
          {showBackArrow && <BackIcon className="bi bi-arrow-left" onClick={() => navigate('/setting')}></BackIcon>}
          <h5>{metaType.toUpperCase()}</h5>
        </div>
        <div className="d-flex align-items-center ">
          <Button
            className="ms-2"
            type="outline-primary"
            onClick={() => {
              setModalType(ModalType.ADD_EDIT);
              setItems(null);
            }}
          >
            ADD NEW
          </Button>
        </div>
      </div> */}
      <PageHeader
        title={
          <>
            Meta Management -{" "}
            <span className="text-secondary">
              {(getCurrentMenuItem()?.label || metaType).toUpperCase()}
            </span>
          </>
        }
        buttonText="ADD NEW"
        onButtonClick={() => {
          setModalType(ModalType.ADD_EDIT);
          setItems(null);
        }}
        backPath="/setting"
      />
      <div className="m-0 p-4 row" style={{ alignItems: "start" }}>
        <SidebarNavigation
          menuItems={sidebarMenuItems}
          activeItem={activeSection}
          onItemClick={setActiveSection}
          className="col-sm-5 col-md-4 col-lg-3 ps-0 sidebar-top"
        />
        <div className="col-sm-7 col-md-8 col-lg-9 gap-3">
          <div className="card mx-0 mb-4 pb-3">
            <div className="d-flex align-items-center" ref={languageRef}>
              <div className="d-flex align-items-center justify-content-between w-100 me-3">
                {/* <h6 className="text-uppercase mb-0">Languages</h6> */}
              </div>
            </div>
            <div className="d-flex align-items-center border-bottom p-3 bg-light">
              <input
                type="text"
                className="form-control"
                placeholder={`Search ${
                  currentItemId === "language" ? "languages" : "categories"
                }...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <i
                  className="bi bi-x-circle iconSvg clear"
                  onClick={() => setSearch("")}
                ></i>
              )}
            </div>
            {filteredList.length === 0 ? (
              <div className="text-muted text-center py-3">
                No {metaType} found.
              </div>
            ) : (
              filteredList.map((item: any) => {
                const displayValue =
                  item.name ||
                  item.title ||
                  item.label ||
                  item.id ||
                  item.value ||
                  item.text ||
                  JSON.stringify(item);
                const itemKey = item.id || item.nid || item.name;
                const isExpandable =
                  Array.isArray(item.sub_meta) && item.sub_meta.length > 0;
                const isExpanded = expandedCategory === itemKey;
                return (
                  <div
                    key={itemKey}
                    className="px-2 py-3 border-bottom align-items-center catRow"
                  >
                    <div
                      className={`d-flex justify-content-between align-items-center ${
                        isExpandable ? "pointer" : ""
                      }`}
                      onClick={() =>
                        isExpandable &&
                        setExpandedCategory(isExpanded ? null : itemKey)
                      }
                    >
                      <span
                        style={{ marginLeft: !isExpandable ? "15px" : "0px" }}
                      >
                        {isExpandable && (
                          <i
                            className={`${
                              isExpanded
                                ? "bi bi-caret-down-fill "
                                : "bi bi-caret-right-fill"
                            }`}
                            style={{ marginRight: "5px" }}
                          ></i>
                        )}

                        {displayValue}
                      </span>
                      <div className="dropdown">
                        <MoreVert
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoreOptionsToggle(itemKey);
                          }}
                          className="dropdown-toggle iconSvg"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        />
                        {openDropdown === itemKey && (
                          <div ref={moreOptionsRef} style={{ zIndex: 1000 }}>
                            <ul
                              className="dropdown-menu "
                              style={{
                                listStyle: "none",
                                display: "block",
                                right: "0",
                                width: "100px",
                              }}
                            >
                              {listMoreActions.map((option) => (
                                <li
                                  key={option}
                                  className="text-capitalize"
                                  data-value={option}
                                  onClick={() =>
                                    handleMoreOptions(option, item)
                                  }
                                >
                                  <a className="dropdown-item">{option}</a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    {isExpandable && isExpanded && (
                      <div className="mt-1">
                        {item.sub_meta.map((sub: any, idx: number) => {
                          const subDisplay =
                            sub.name ||
                            sub.title ||
                            sub.label ||
                            sub.id ||
                            sub.value ||
                            sub.text ||
                            JSON.stringify(sub);
                          const subKey = `${itemKey}-${idx}`;
                          return (
                            <div
                              className="border-bottom"
                              key={idx}
                              style={{
                                // borderTop: idx !== 0 ? 'none' : '1px solid #ccc',
                                borderBottom:
                                  idx === item.sub_meta.length - 1
                                    ? "none"
                                    : "",
                              }}
                            >
                              <div className="py-3 ms-4 d-flex justify-content-between align-items-center">
                                <span>{subDisplay}</span>

                                <div className="dropdown">
                                  <MoreVert
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubMetaMoreOptionsToggle(subKey);
                                    }}
                                    className="dropdown-toggle iconSvg"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  />
                                  {openSubDropdown === subKey && (
                                    <div
                                      ref={subMetaMoreOptionsRef}
                                      style={{ zIndex: 1000 }}
                                    >
                                      <ul
                                        className="dropdown-menu "
                                        style={{
                                          listStyle: "none",
                                          display: "block",
                                          right: "0",
                                          width: "100px",
                                        }}
                                      >
                                        {subMetaList.map((option) => (
                                          <li
                                            key={option}
                                            className="text-capitalize py-1 px-3"
                                            data-value={option}
                                            onClick={(e) =>
                                              handleSubMetaMoreOptions(
                                                e,
                                                option,
                                                item,
                                                sub
                                              )
                                            }
                                          >
                                            {option}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {/* Single Modal Component */}
      {modalType !== ModalType.NONE && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={getModalTitle()}
          width="500px"
          height="auto"
          footer={getModalFooter()}
          closeOnOutsideClick={false}
        >
          {getModalContent()}
        </Modal>
      )}
      {modalType === ModalType.CONFIRM && (
        <AlertDialog
          isOpen={true}
          onClose={closeModal}
          onConfirm={() => {
            if (isDelete) {
              handleDeleteConfirm();
            } else {
              handleDelete();
            }
            closeModal();
          }}
          title="ALERT!"
          message="Are you sure you want to delete this content?"
          confirmText="Confirm"
          cancelText="Cancel"
          confirmButtonType="danger"
        />
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  categories: state.app.categories,
  categoryList: state.media.categoryList,
  bunchCategoryList: state.constants.bunchCategoryList.list,
  bunchLanguageList: state.constants.bunchLanguageList.list,
  tag: state.app.tag,
  cast: state.app.cast,
  genre: state.app.genre,
});

const mapDispatch = {
  createCategory,
  updateCategory,
  getCategoryList,
  deleteCategory,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(MetaManagement);
